import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { PersonalOfferModel, ResponsiveImageModel } from 'autogen/swagger/all';
import { TermsButtonText } from 'autogen/translation-keys/trans-website-myprofile-frontpage-personaloffer';
import { ShopNow } from 'autogen/translation-keys/trans-website-voucher';
import ContentCard from 'components/ContentCard';
import type { ContentCardProps, ImageAspectRatio } from 'components/ContentCard/types';
import { mapResponsiveImageModelToImageProps } from 'components/generic/Image/helpers/mapToImageProps';
import { Typography } from 'components/generic/typography';
import QuickView, { useQuickView } from 'components/quickView';
import { customScrollbar } from 'modules/helpers/mixins';
import { breakpoint } from 'variables';

const StyledQuickView = styled(QuickView)`
    // && for specificity
    && {
        ${breakpoint.up('768px')} {
            min-width: 480px;
            width: 480px;
        }
    }
`;

const ScrollContainer = styled.div`
    padding: ${({ theme }) => `0 ${theme.Spacing24} ${theme.Spacing24}`};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing24};

    ${customScrollbar}
`;

function getImageProps(
    altText: string,
    imageUrl?: string,
    defaultImage?: ResponsiveImageModel
): ContentCardProps['image'] | undefined {
    if (!imageUrl && !defaultImage) return undefined;

    if (imageUrl) {
        return { src: imageUrl, alt: altText, transformType: 'WidthScale', aspectRatio: 'expand' };
    }

    if (defaultImage) {
        return {
            ...mapResponsiveImageModelToImageProps(defaultImage),
            transformType: 'WidthScale',
            aspectRatio: 'expand'
        };
    }
}

export default function MemberVoucher({
    model: { desktopImageUrl, defaultImage, linkAction, name, activeTimeText, disclaimer, tag }
}: {
    model: PersonalOfferModel;
}) {
    const { t } = useTranslation();
    const { toggle } = useQuickView(`MemberVoucherTerms-${name}`);

    const imageProps = useMemo(() => getImageProps(name, desktopImageUrl, defaultImage), []);
    const linkProps: ContentCardProps['link'] = {
        action: { $type: 'LinkActionType', action: linkAction },
        label: t(ShopNow),
        variant: 'primary',
        showFirstInActions: true
    };
    const actionsProps: ContentCardProps['actions'] = [
        {
            label: t(TermsButtonText),
            variant: 'tertiary',
            onClick: () => toggle(true)
        }
    ];
    const tagsProps = tag ? [tag] : undefined;
    const customAspectRatio = '800/930' as ImageAspectRatio; // Special case for MemberVoucher to get 800x930 ratio

    return (
        <>
            <ContentCard
                image={imageProps ? { ...imageProps, aspectRatio: customAspectRatio } : undefined}
                heading={name}
                subheading={activeTimeText}
                link={linkProps}
                actions={disclaimer ? actionsProps : undefined}
                tags={tagsProps}
            />
            {disclaimer && (
                <StyledQuickView quickViewId={`MemberVoucherTerms-${name}`} position="right">
                    <ScrollContainer>
                        <Typography.Headline size="M">{name}</Typography.Headline>
                        <Typography.Body>{disclaimer}</Typography.Body>
                    </ScrollContainer>
                </StyledQuickView>
            )}
        </>
    );
}
